import React, { useState, useRef } from "react";
import { IoMdClose } from "react-icons/io";
import { TbArrowBackUp, TbDeviceFloppy, TbPalette, TbSparkles } from "react-icons/tb";
import {
  MdOutlineWallpaper,
  MdOutlineHeadsetMic,
  MdOutlineCategory,
  MdOutlineTab,
  MdOutlineCardGiftcard,
  MdOutlineTouchApp,
  MdOutlineRule,
  MdOutlineHistory,
  MdOutlineCollectionsBookmark,
  MdOutlineCheckCircleOutline,
  MdOutlineHourglassTop,
  MdOutlineSettings,
  MdOutlineImage,
  MdOutlineAcUnit,
  MdOutlinePerson,
  MdOutlineSearch,
  MdExpandMore,
  MdOutlineGradient,
  MdOutlineCircle,
  MdOutlineNotificationsActive,
  MdOutlineLabel,
  MdOutlineEngineering,
  MdOutlineErrorOutline,
} from "react-icons/md";
import { CircularProgress } from "@mui/material";
import { useThemeManager } from "../hooks/useThemeManager";
import { useCss } from "../context/CssContext";
import { useStatic } from "../context/StaticContext";
import ThemeSection from "./ThemeSection";
import RangeSlider from "./RangeSlider";
import ThemeSelector from "./ThemeSelector";
import SuccessAlert from "./alerts/SuccessAlert";
import ErrorAlert from "./alerts/ErrorAlert";
import config from "../config";
import "./CssPanel.css";

// ── Sezonluk Mod listesi ────────────────────────────────────────
const SEASONAL_MODES = [
  { id: 'none',      emoji: '🚫', label: 'Kapalı',      title: 'Sezonluk efekt yok' },
  { id: 'snow',      emoji: '❄️', label: 'Kar',         title: 'Yılbaşı - Kar yağışı' },
  { id: 'halloween', emoji: '🎃', label: 'Cadılar',     title: 'Cadılar Bayramı - Yarasalar' },
  { id: 'valentine', emoji: '❤️', label: 'Sevgililer',  title: 'Sevgililer Günü - Kalpler' },
  { id: 'luck',      emoji: '🍀', label: 'Şans',        title: 'Şans Modu - Yoncalar & altın' },
  { id: 'fireworks', emoji: '🎆', label: 'Fişek',       title: 'Yılbaşı - Havai fişek patlamaları' },
  { id: 'ramadan',   emoji: '🌙', label: 'Ramazan',     title: 'Ramazan / Bayram - Hilal & yıldızlar' },
  { id: 'kurban',    emoji: '🐑', label: 'Kurban',      title: 'Kurban Bayramı - Süzülen kuzular & hafif yıldız yağışı' },
  { id: 'matchday',  emoji: '⚽', label: 'Maç Günü',    title: 'Maç Günü - Konfeti & kupalar' },
];

// ── Bölüm meta verisi (başlık + ikon) ───────────────────────────
const SECTION_META = {
  general:             { title: "Genel Ayarlar",        icon: <MdOutlineSettings size={16} /> },
  appBackground:       { title: "Arka Plan Aura Katmanları", icon: <MdOutlineGradient size={16} /> },
  header:              { title: "Header Ayarları",      icon: <MdOutlineHeadsetMic size={16} /> },
  mainCategory:        { title: "Ana Kategori",         icon: <MdOutlineCategory size={16} /> },
  tab:                 { title: "Tab / Alt Kategori",   icon: <MdOutlineTab size={16} /> },
  bonusCard:           { title: "Bonus Kart Görünümü",  icon: <MdOutlineCardGiftcard size={16} /> },
  bonusBadges:         { title: "Bonus Tipi Rozet Renkleri", icon: <MdOutlineLabel size={16} /> },
  bonusButtons:        { title: "Bonus Kart Butonları", icon: <MdOutlineTouchApp size={16} /> },
  rulesPopup:          { title: "Kurallar Popup'ı",     icon: <MdOutlineRule size={16} /> },
  historyPopup:        { title: "Geçmiş Popup'ı",       icon: <MdOutlineHistory size={16} /> },
  multipleBonusDialog: { title: "Çoklu Bonus Dialog'u", icon: <MdOutlineCollectionsBookmark size={16} /> },
  bonusConfirmPopup:   { title: "Bonus Onay Popup'ı",   icon: <MdOutlineCheckCircleOutline size={16} /> },
  loadingPage:         { title: "Loading Sayfası",      icon: <MdOutlineHourglassTop size={16} /> },
  statusColors:        { title: "Durum Renkleri",       icon: <MdOutlineCircle size={16} /> },
  alerts:              { title: "Bildirim Alert'leri",  icon: <MdOutlineNotificationsActive size={16} /> },
  badge:               { title: "Sayaç Rozeti",         icon: <MdOutlineLabel size={16} /> },
  maintenance:         { title: "Bakım Modu",           icon: <MdOutlineEngineering size={16} /> },
  errorPage:           { title: "Hata Sayfası",         icon: <MdOutlineErrorOutline size={16} /> },
};

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('http')) return url;
  const prefix = config.imgUrl && config.imgUrl.endsWith('/') ? config.imgUrl : `${config.imgUrl || ''}/`;
  const cleanedImg = url.startsWith('/') ? url.slice(1) : url;
  return `${prefix}${cleanedImg}`;
};

const CssPanelNew = ({ open, onClose }) => {
  const { themeValues, themeConfig, flatConfig, updateThemeValue, resetToDefaults } =
    useThemeManager();
  const {
    fetchAndApplyCss,
    customBgImage,
    customBgEnabled,
    setCustomBgEnabled,
    updateCustomBg,
    removeCustomBg,
    customLogoImage,
    customLogoEnabled,
    setCustomLogoEnabled,
    updateCustomLogo,
    removeCustomLogo,
    customAvatarImage,
    customAvatarEnabled,
    setCustomAvatarEnabled,
    updateCustomAvatar,
    removeCustomAvatar,
    longLogoVisible,
    setLongLogoVisible,
    longLogoVariant,
    setLongLogoVariant,
    seasonalMode,
    setSeasonalMode,
    cardLayoutMode,
    setCardLayoutMode,
    cardBadgePosition = 'left',
    setCardBadgePosition,
    setBonusCardGoldMode,
    setTabGoldMode,
  } = useCss();

  // Banner carousel state from context
  const {
    bannerImages,
    addBannerImage,
    removeBannerImage,
    clearBannerImages,
  } = useCss();

  const {
    bonusBannerVisible,
    setBonusBannerVisible,
  } = useStatic();

  const [loading, setLoading] = useState(false);
  const [activeThemeId, setActiveThemeId] = useState(null);
  const [bgUploading, setBgUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [logoSettingsOpen, setLogoSettingsOpen] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const bgFileInputRef = useRef(null);
  const logoFileInputRef = useRef(null);
  const avatarFileInputRef = useRef(null);
  const bannerFileInputRef = useRef(null);

  const uploadToServer = async (base64Data, customFileName = null, folderName = "") => {
    const base64String = base64Data.split(',')[1];
    const external_key = localStorage.getItem('external_key');
    if (!external_key) {
      throw new Error('Oturum bilgisi bulunamadı, lütfen tekrar giriş yapın!');
    }
    const response = await fetch(`${config.apiBaseUrl3}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        process_id: 'upload_image',
        data: base64String,
        external_key,
        file_name: customFileName,
        folder: folderName
      }),
      mode: 'cors',
      credentials: 'include',
    });
    const text = await response.text();
    if (!text || !text.trim()) {
      throw new Error(
        `Sunucu boş cevap döndü (HTTP ${response.status}). Görsel çok büyük olabilir — daha küçük bir görsel deneyin.`
      );
    }
    let result;
    try {
      result = JSON.parse(text);
    } catch {
      throw new Error('Sunucudan geçersiz cevap alındı. Lütfen tekrar deneyin.');
    }
    if (!result.status) throw new Error(result.message || 'Görsel yükleme başarısız!');
    return result.filePath;
  };

  const deleteImageFromServer = async (filePath) => {
    if (!filePath || typeof filePath !== 'string' || !filePath.startsWith('/assets')) return;
    try {
      const external_key = localStorage.getItem('external_key');
      if (!external_key) return;
      await fetch(`${config.apiBaseUrl3}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          process_id: 'delete_image',
          filePath,
          external_key
        }),
        mode: 'cors',
        credentials: 'include',
      });
    } catch (err) {
      console.error("Görsel sunucudan silinirken hata oluştu:", err);
    }
  };

  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  React.useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const bonusButtonsGoldModeValue = themeValues['bonusButtons_goldMode'];
  React.useEffect(() => {
    if (bonusButtonsGoldModeValue !== undefined) {
      setBonusCardGoldMode(bonusButtonsGoldModeValue === 'true' || bonusButtonsGoldModeValue === true);
    }
  }, [bonusButtonsGoldModeValue, setBonusCardGoldMode]);

  const tabGoldModeValue = themeValues['tab_goldMode'];
  React.useEffect(() => {
    if (tabGoldModeValue !== undefined) {
      setTabGoldMode(tabGoldModeValue === 'true' || tabGoldModeValue === true);
    }
  }, [tabGoldModeValue, setTabGoldMode]);

  if (!open) return null;

  // ── Görsel sıkıştırma ──────────────────────────────────────
  const compressBgImage = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX_W = 1920;
      let { width, height } = img;
      // Sadece gerçekten büyükse downscale et — orijinali bozma
      if (width > MAX_W) {
        height = Math.round((height * MAX_W) / width);
        width = MAX_W;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      // Yüksek kaliteli downscale için smoothing'i en üst seviyeye al
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      // WebP varsa onu kullan (JPEG'den çok daha iyi kalite/boyut), yoksa yüksek kalite JPEG
      const webp = canvas.toDataURL('image/webp', 0.92);
      if (webp && webp.indexOf('data:image/webp') === 0) {
        resolve(webp);
      } else {
        resolve(canvas.toDataURL('image/jpeg', 0.95));
      }
    };
    img.onerror = reject;
    img.src = url;
  });

  const compressLogoImage = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX_W = 600;
      let { width, height } = img;
      if (width > MAX_W) {
        height = Math.round((height * MAX_W) / width);
        width = MAX_W;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      // Logo'da transparency olabilir; PNG kalmalı (lossless)
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = url;
  });

  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Lütfen geçerli bir görsel dosyası seçin');
      return;
    }
    setAvatarUploading(true);
    try {
      const compressed = await compressLogoImage(file);
      const filePath = await uploadToServer(compressed, null, 'css_panel');
      if (customAvatarImage && customAvatarImage.startsWith('/assets')) {
        await deleteImageFromServer(customAvatarImage);
      }
      updateCustomAvatar(filePath);
      setSuccessMessage('Avatar yüklendi — kaydetmek için "CSS Kaydet" butonuna basın');
    } catch (err) {
      setErrorMessage(err.message || 'Görsel işlenirken bir hata oluştu');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Lütfen geçerli bir görsel dosyası seçin');
      return;
    }

    setLogoUploading(true);
    try {
      const compressed = await compressLogoImage(file);
      const filePath = await uploadToServer(compressed, null, 'css_panel');
      if (customLogoImage && customLogoImage.startsWith('/assets')) {
        await deleteImageFromServer(customLogoImage);
      }
      updateCustomLogo(filePath);
      setSuccessMessage('Logo yüklendi — kaydetmek için "CSS Kaydet" butonuna basın');
    } catch (err) {
      setErrorMessage(err.message || 'Görsel işlenirken bir hata oluştu');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleBgFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Lütfen geçerli bir görsel dosyası seçin');
      return;
    }

    setBgUploading(true);
    try {
      const compressed = await compressBgImage(file);
      const filePath = await uploadToServer(compressed, null, 'css_panel');
      if (customBgImage && customBgImage.startsWith('/assets')) {
        await deleteImageFromServer(customBgImage);
      }
      updateCustomBg(filePath);
      setSuccessMessage('Arkaplan görseli yüklendi — kaydetmek için "CSS Kaydet" butonuna basın');
    } catch (err) {
      setErrorMessage(err.message || 'Görsel işlenirken bir hata oluştu');
    } finally {
      setBgUploading(false);
    }
  };

  // Banner görsel yükleme
  const handleBannerFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Lütfen geçerli bir görsel dosyası seçin');
      return;
    }
    setBannerUploading(true);
    try {
      const compressed = await compressBgImage(file);
      const filePath = await uploadToServer(compressed, null, 'css_panel');
      addBannerImage(filePath);
      setSuccessMessage('Banner görseli eklendi — kaydetmek için "CSS Kaydet" butonuna basın');
    } catch (err) {
      setErrorMessage(err.message || 'Görsel işlenirken bir hata oluştu');
    } finally {
      setBannerUploading(false);
    }
  };

  // ── CSS Kaydet ──────────────────────────────────────────
  const saveCssToApi = async () => {
    setLoading(true);

    try {
      const cssData = {};

      Object.keys(flatConfig).forEach((key) => {
        const cfg = flatConfig[key];
        if (cfg && cfg.cssVar) {
          cssData[cfg.cssVar] = themeValues[key];
        }
      });

      const getPV = (v) => document.documentElement.style.getPropertyValue(v) || null;
      cssData['--primary-color']       = getPV('--primary-color');
      cssData['--primary-color-rgb']   = getPV('--primary-color-rgb');
      cssData['--secondary-color']     = getPV('--secondary-color');
      cssData['--primary-tint-08']     = getPV('--primary-tint-08');
      cssData['--primary-tint-16']     = getPV('--primary-tint-16');
      cssData['--primary-border-22']   = getPV('--primary-border-22');
      cssData['--primary-border-38']   = getPV('--primary-border-38');

      cssData['customBgImage'] = customBgImage ?? null;
      cssData['customBgEnabled'] = customBgEnabled ?? false;
      cssData['customLogoImage'] = customLogoImage ?? null;
      cssData['customLogoEnabled'] = customLogoEnabled ?? false;
      cssData['customAvatarImage'] = customAvatarImage ?? null;
      cssData['customAvatarEnabled'] = customAvatarEnabled ?? false;
      cssData['longLogoVisible'] = longLogoVisible ?? true;
      cssData['longLogoVariant'] = longLogoVariant === 'white' ? 'white' : 'black';
      cssData['seasonalMode'] = seasonalMode ?? 'none';
      // Geriye dönük uyumluluk: snowEnabled hala bekleniyorsa
      cssData['snowEnabled'] = seasonalMode === 'snow';
      // Bonus kart tasarım modu — 'image' (varsayılan) | 'text-only' (görselsiz)
      cssData['cardLayoutMode'] = cardLayoutMode === 'text-only' ? 'text-only' : 'image';
      cssData['cardBadgePosition'] = ['left', 'right', 'none'].includes(cardBadgePosition) ? cardBadgePosition : 'left';
      // Banner carousel verileri
      cssData['bannerImages'] = bannerImages || [];
      // bannerEnabled artık set_static ile kaydedildiği için cssData'ya eklemiyoruz

      const response = await fetch(`${config.adminApi}/set_css`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: cssData }),
        mode: 'cors',
        credentials: 'include',
      });

      const result = await response.json();

      if (response.ok && result.status) {
        // CSS başarıyla kaydedildiyse banner görünürlüğünü de admin statik ayarlarına kaydet
        try {
          await fetch(`${config.adminApi}/set_static`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'bool',
              key: 'bonus_banner_visible',
              value: bonusBannerVisible,
            }),
            mode: 'cors',
            credentials: 'include',
          });
        } catch (staticErr) {
          console.error("Banner durumu kaydedilirken hata:", staticErr);
        }

        setSuccessMessage(result.message || "CSS ayarları başarıyla kaydedildi!");
        setErrorMessage('');
        await fetchAndApplyCss();
      } else {
        setErrorMessage(result.message || 'CSS kaydetme hatası oluştu');
        setSuccessMessage('');
      }
    } catch (error) {
      console.error('CSS kaydetme hatası:', error);
      setErrorMessage("Bağlantı hatası! Lütfen tekrar deneyin.");
      setSuccessMessage('');
    } finally {
      setLoading(false);
      setShowSaveModal(false);
    }
  };

  const handleConfirmedReset = () => {
    resetToDefaults();
    setShowResetModal(false);
    setSuccessMessage("Ayarlar varsayılan değerlere sıfırlandı!");
    setErrorMessage('');
  };

  // ── Tema uygula ──────────────────────────────────────────
  const handleApplyTheme = (theme) => {
    setActiveThemeId(theme.id);

    const hexToRgb = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `${r}, ${g}, ${b}`;
    };

    const p = theme.primary;
    const s = theme.secondary;
    const bg = theme.bg;
    const pRgb = hexToRgb(p);

    const tint08 = `rgba(${pRgb},0.08)`;
    const tint16 = `rgba(${pRgb},0.16)`;
    const brd22  = `rgba(${pRgb},0.22)`;
    const brd38  = `rgba(${pRgb},0.38)`;

    document.documentElement.style.setProperty('--primary-color',     p);
    document.documentElement.style.setProperty('--primary-color-rgb', pRgb);
    document.documentElement.style.setProperty('--secondary-color',   s);
    document.documentElement.style.setProperty('--primary-tint-08',   tint08);
    document.documentElement.style.setProperty('--primary-tint-16',   tint16);
    document.documentElement.style.setProperty('--primary-border-22', brd22);
    document.documentElement.style.setProperty('--primary-border-38', brd38);

    const vars = {
      'general_backgroundColor':                       bg,
      /* Ana & alt kategori butonları */
      'mainCategory_bgColor':                          tint08,
      'mainCategory_activeBgColor':                    p,
      'mainCategory_activeGlowColor':                  `rgba(${pRgb},0.40)`,
      'tab_bgColor':                                   tint08,
      'tab_activeBgColor':                             p,
      'tab_activeGlowColor':                           `rgba(${pRgb},0.40)`,
      /* Bonus kart */
      'bonusCard_color':                               `rgba(${pRgb},0.50)`,
      'bonusCard_borderColor':                         `rgba(${pRgb},0.18)`,
      'bonusButtons_actionBarHoverBgColor':            `rgba(${pRgb},0.22)`,
      /* Popup'lar — glass tema */
      'rulesPopup_backgroundColor':                    tint08,
      'rulesPopup_borderColor':                        `rgba(${pRgb},0.20)`,
      'rulesPopup_badgeBgColor':                       `rgba(${pRgb},0.15)`,
      'rulesPopup_badgeBorderColor':                   brd22,
      'rulesPopup_badgeTextColor':                     p,
      'rulesPopup_ruleBgColor':                        `rgba(${pRgb},0.07)`,
      'rulesPopup_ruleBorderColor':                    `rgba(${pRgb},0.15)`,
      'rulesPopup_ruleAccentColor':                    p,
      'historyPopup_backgroundColor':                  tint08,
      'historyPopup_borderColor':                      `rgba(${pRgb},0.20)`,
      'historyPopup_itemBgColor':                      `rgba(${pRgb},0.07)`,
      'historyPopup_itemBorderColor':                  `rgba(${pRgb},0.14)`,
      'bonusConfirmPopup_popupBgColor':                tint08,
      'bonusConfirmPopup_popupBorderColor':            brd22,
      'bonusConfirmPopup_rulesBtnBgColor':             `rgba(${pRgb},0.10)`,
      'bonusConfirmPopup_rulesBtnBorderColor':         brd22,
      'bonusConfirmPopup_rulesBtnTextColor':           p,
      'bonusConfirmPopup_confirmButtonBgColor':        `linear-gradient(135deg, ${p} 0%, ${s} 100%)`,
      'multipleBonusDialog_backgroundColor':           tint08,
      'multipleBonusDialog_iconColor':                 p,
      'multipleBonusDialog_optionButtonBgColor':       `linear-gradient(135deg, ${p} 0%, ${s} 100%)`,
      'multipleBonusDialog_optionButtonHoverBgColor':  `linear-gradient(135deg, ${s} 0%, ${p} 100%)`,
      'multipleBonusDialog_optionButtonGlowColor':     `rgba(${pRgb},0.40)`,
      'loadingPage_spinnerColor':                      p,
      /* Arka plan aura katmanları — primary'den türetilir, accent sabit */
      'appBackground_primaryAura':                     `rgba(${pRgb},0.07)`,
      'appBackground_bottomAura':                      `rgba(${pRgb},0.04)`,
    };

    Object.entries(vars).forEach(([key, value]) => {
      updateThemeValue(key, value);
    });

    setSuccessMessage(`"${theme.name}" teması uygulandı — kaydetmek için CSS Kaydet butonuna basın`);
  };

  // ── Bölümleri belirli sırayla render et ─────────────────────
  const SECTION_ORDER = [
    'general', 'appBackground', 'header', 'mainCategory', 'tab',
    'bonusCard', 'bonusBadges', 'bonusButtons',
    'rulesPopup', 'historyPopup', 'multipleBonusDialog', 'bonusConfirmPopup',
    'loadingPage',
    'statusColors', 'alerts', 'badge', 'maintenance', 'errorPage',
  ];

  const isAnyUploading = bgUploading || logoUploading || avatarUploading || bannerUploading;

  return (
    <div className="css-panel">
      {/* ── Alert Mesajları ── */}
      {successMessage && <SuccessAlert message={successMessage} />}
      {errorMessage && <ErrorAlert message={errorMessage} />}

      {/* ── Header ── */}
      <div className="css-panel-header">
        <div className="css-panel-brand">
          <div className="css-panel-brand-icon">
            <TbPalette size={20} />
          </div>
          <div className="css-panel-titles">
            <h2>CSS Düzenleme Paneli</h2>
            <div className="css-panel-subtitle">Arayüz görünümünü kişiselleştir</div>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="icon-btn save-icon-btn"
            onClick={() => setShowSaveModal(true)}
            disabled={loading || isAnyUploading}
            title="CSS ayarlarını kaydet"
          >
            {loading ? (
              <CircularProgress size={16} sx={{ color: '#4ade80' }} />
            ) : (
              <TbDeviceFloppy size={18} />
            )}
          </button>
          <button
            className="icon-btn reset-icon-btn"
            onClick={() => setShowResetModal(true)}
            disabled={loading || isAnyUploading}
            title="Varsayılan ayarlara dön"
          >
            <TbArrowBackUp size={18} />
          </button>
          <button className="icon-btn close-icon-btn" onClick={onClose} title="Kapat">
            <IoMdClose size={20} />
          </button>
        </div>
      </div>

      {/* ── Search Bar ── */}
      <div className="css-panel-search">
        <div className="css-search-wrap">
          <MdOutlineSearch size={16} className="css-search-icon" />
          <input
            type="text"
            className="css-search-input"
            placeholder="Ayar ara... (örn: arka plan, başlık, buton)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="css-search-clear"
              onClick={() => setSearchTerm('')}
              title="Temizle"
            >
              <IoMdClose size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ── İçerik ── */}
      <div className="css-panel-content">

        {/* ── Tema Seçici ── */}
        {!searchTerm && (
          <div className="css-section expanded">
            <div className="section-header" style={{ cursor: 'default' }}>
              <div className="section-header-left">
                <div className="section-icon-wrap"><TbSparkles size={16} /></div>
                <h3>Hazır Temalar</h3>
              </div>
            </div>
            <div className="section-content" style={{ padding: '8px 14px 14px' }}>
              <ThemeSelector
                onApplyTheme={handleApplyTheme}
                activeThemeId={activeThemeId}
              />
            </div>
          </div>
        )}

        {/* ── Banner Carousel Yönetimi ── */}
        {!searchTerm && (
          <div className="bg-upload-section">
            <div className="bg-section-title">
              <div className="bg-section-title-icon"><MdOutlineImage size={15} /></div>
              Banner Carousel
            </div>

            {/* Banner Görselleri Grid */}
            <div className="banner-images-grid">
              {bannerImages.length > 0 ? (
                bannerImages.map((img, idx) => (
                  <div key={idx} className="banner-image-item">
                    <img src={getImageUrl(img)} alt={`Banner ${idx + 1}`} className="banner-image-preview" />
                    <button
                      className="banner-image-remove"
                      onClick={async () => {
                        const imgPath = bannerImages[idx];
                        if (imgPath && imgPath.startsWith('/assets')) {
                          await deleteImageFromServer(imgPath);
                        }
                        removeBannerImage(idx);
                      }}
                      title="Kaldır"
                      disabled={bannerUploading}
                    >
                      <IoMdClose size={12} />
                    </button>
                    <span className="banner-image-index">{idx + 1}</span>
                  </div>
                ))
              ) : (
                <div className="bg-preview-empty" style={{ gridColumn: '1 / -1', minHeight: '80px' }}>
                  <MdOutlineImage size={26} />
                  <span>Henüz banner görseli eklenmedi</span>
                </div>
              )}
            </div>

            {/* Banner Yükleme */}
            <div className="bg-controls-row">
              <input
                ref={bannerFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleBannerFileChange}
                style={{ display: 'none' }}
              />
              <button
                className={`bg-select-btn${bannerUploading ? ' loading' : ''}`}
                onClick={() => bannerFileInputRef.current?.click()}
                disabled={bannerUploading}
              >
                {bannerUploading ? (
                  <><span className="bg-spinner" /> Yükleniyor...</>
                ) : (
                  <>📤 Banner Ekle</>
                )}
              </button>
              {bannerImages.length > 0 && (
                <button
                  className="bg-remove-btn"
                  onClick={async () => {
                    for (const img of bannerImages) {
                      if (img && img.startsWith('/assets')) {
                        await deleteImageFromServer(img);
                      }
                    }
                    clearBannerImages();
                  }}
                  title="Tüm Banner Görsellerini Sil"
                  disabled={bannerUploading}
                >
                  <IoMdClose size={15} /> Tümünü Sil
                </button>
              )}
            </div>

            {/* Banner Aç/Kapa Toggle */}
            <div
              className="bg-toggle-row"
              style={{ pointerEvents: bannerUploading ? 'none' : 'auto' }}
              onClick={() => setBonusBannerVisible(!bonusBannerVisible)}
            >
              <div className={`bg-toggle-switch${bonusBannerVisible ? ' on' : ''}`}>
                <div className="bg-toggle-knob" />
              </div>
              <span className="bg-toggle-label">
                {bonusBannerVisible ? 'Banner Aktif' : 'Banner Devre Dışı'}
              </span>
            </div>

            <p className="seasonal-mode-hint">
              {bannerImages.length === 0
                ? 'Banner görseli ekleyerek ana sayfada slider oluşturabilirsiniz.'
                : `${bannerImages.length} görsel yüklendi. Kaydetmek için "CSS Kaydet" butonuna basın.`}
            </p>
          </div>
        )}

        {/* ── Arkaplan Görseli ── */}
        {!searchTerm && (
          <div className="bg-upload-section">
            <div className="bg-section-title">
              <div className="bg-section-title-icon"><MdOutlineWallpaper size={15} /></div>
              Arkaplan Görseli
            </div>

            <div className="bg-preview-box">
              {customBgImage ? (
                <img src={getImageUrl(customBgImage)} alt="Arkaplan" className="bg-preview-img" />
              ) : (
                <div className="bg-preview-empty">
                  <MdOutlineImage size={26} />
                  <span>Henüz görsel yüklenmedi</span>
                </div>
              )}
            </div>

            <div className="bg-controls-row">
              <input
                ref={bgFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleBgFileChange}
                style={{ display: 'none' }}
              />
              <button
                className={`bg-select-btn${bgUploading ? ' loading' : ''}`}
                onClick={() => bgFileInputRef.current?.click()}
                disabled={bgUploading}
              >
                {bgUploading ? (
                  <><span className="bg-spinner" /> Yükleniyor...</>
                ) : (
                  <>📤 Görsel Seç</>
                )}
              </button>
              {customBgImage && (
                <button
                  className="bg-remove-btn"
                  onClick={async () => {
                    if (customBgImage.startsWith('/assets')) {
                      await deleteImageFromServer(customBgImage);
                    }
                    removeCustomBg();
                  }}
                  disabled={bgUploading}
                >
                  🗑️ Kaldır
                </button>
              )}
            </div>

            <div
              className="bg-toggle-row"
              style={{ pointerEvents: bgUploading ? 'none' : 'auto' }}
              onClick={() => setCustomBgEnabled(!customBgEnabled)}
            >
              <div className={`bg-toggle-switch${customBgEnabled ? ' on' : ''}`}>
                <div className="bg-toggle-knob" />
              </div>
              <span className="bg-toggle-label">
                {customBgEnabled ? 'Özel Arkaplan Aktif' : 'Özel Arkaplan Devre Dışı'}
              </span>
            </div>

            {!customBgEnabled && (
              <div className="bg-color-row">
                <span className="bg-color-label">Arka Plan Rengi</span>
                <div className="bg-color-input-wrap">
                  <input
                    type="color"
                    value={themeValues['general_backgroundColor'] || '#080d19'}
                    onChange={(e) => updateThemeValue('general_backgroundColor', e.target.value)}
                    className="bg-color-input"
                  />
                  <span className="bg-color-value">
                    {themeValues['general_backgroundColor'] || '#080d19'}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Logo Görseli ── */}
        {!searchTerm && (
          <div className="bg-upload-section">
            <div className="bg-section-title">
              <div className="bg-section-title-icon"><MdOutlineImage size={15} /></div>
              Logo Görseli
            </div>

            <div className="bg-preview-box">
              {customLogoImage ? (
                <img
                  src={getImageUrl(customLogoImage)}
                  alt="Logo"
                  className="bg-preview-img"
                  style={{ objectFit: 'contain', background: 'transparent' }}
                />
              ) : (
                <div className="bg-preview-empty">
                  <MdOutlineImage size={26} />
                  <span>Henüz logo yüklenmedi</span>
                </div>
              )}
            </div>

            <div className="bg-controls-row">
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoFileChange}
                style={{ display: 'none' }}
              />
              <button
                className={`bg-select-btn${logoUploading ? ' loading' : ''}`}
                onClick={() => logoFileInputRef.current?.click()}
                disabled={logoUploading}
              >
                {logoUploading ? (
                  <><span className="bg-spinner" /> Yükleniyor...</>
                ) : (
                  <>📤 Logo Seç</>
                )}
              </button>
              {customLogoImage && (
                <button
                  className="bg-remove-btn"
                  onClick={async () => {
                    if (customLogoImage.startsWith('/assets')) {
                      await deleteImageFromServer(customLogoImage);
                    }
                    removeCustomLogo();
                  }}
                  disabled={logoUploading}
                >
                  🗑️ Kaldır
                </button>
              )}
            </div>

            <div
              className="bg-toggle-row"
              style={{ pointerEvents: logoUploading ? 'none' : 'auto' }}
              onClick={() => setCustomLogoEnabled(!customLogoEnabled)}
            >
              <div className={`bg-toggle-switch${customLogoEnabled ? ' on' : ''}`}>
                <div className="bg-toggle-knob" />
              </div>
              <span className="bg-toggle-label">
                {customLogoEnabled ? 'Özel Logo Aktif' : 'Özel Logo Devre Dışı'}
              </span>
            </div>

            {/* ── Logo Görsel Ayarları (Boyut / Margin) ── */}
            <div className="bg-logo-settings">
              <button
                type="button"
                className={`bg-logo-settings-toggle${logoSettingsOpen ? ' open' : ''}`}
                onClick={() => setLogoSettingsOpen(!logoSettingsOpen)}
              >
                <div className="bg-logo-settings-toggle-left">
                  <div className="bg-logo-settings-icon">
                    <MdOutlineSettings size={14} />
                  </div>
                  <span>Görsel Ayarları</span>
                </div>
                <MdExpandMore size={18} className="bg-logo-settings-chevron" />
              </button>

              {logoSettingsOpen && (
                <div className="bg-logo-settings-panel">
                  <RangeSlider
                    label="Logo Genişliği"
                    value={themeValues.header_logoMaxWidth}
                    onChange={(v) => updateThemeValue('header_logoMaxWidth', v)}
                    min={themeConfig.header.logoMaxWidth.min}
                    max={themeConfig.header.logoMaxWidth.max}
                    step={themeConfig.header.logoMaxWidth.step}
                    unit={themeConfig.header.logoMaxWidth.unit}
                  />
                  <RangeSlider
                    label="Üst Margin"
                    value={themeValues.header_logoMarginTop}
                    onChange={(v) => updateThemeValue('header_logoMarginTop', v)}
                    min={themeConfig.header.logoMarginTop.min}
                    max={themeConfig.header.logoMarginTop.max}
                    step={themeConfig.header.logoMarginTop.step}
                    unit={themeConfig.header.logoMarginTop.unit}
                  />
                  <RangeSlider
                    label="Alt Margin"
                    value={themeValues.header_logoMarginBottom}
                    onChange={(v) => updateThemeValue('header_logoMarginBottom', v)}
                    min={themeConfig.header.logoMarginBottom.min}
                    max={themeConfig.header.logoMarginBottom.max}
                    step={themeConfig.header.logoMarginBottom.step}
                    unit={themeConfig.header.logoMarginBottom.unit}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Avatar Görseli ── */}
        {!searchTerm && (
          <div className="bg-upload-section">
            <div className="bg-section-title">
              <div className="bg-section-title-icon"><MdOutlinePerson size={15} /></div>
              Kullanıcı Avatarı
            </div>

            <div className="bg-preview-box">
              <img
                src={getImageUrl(customAvatarImage) || '/static/image/default-avatar.png'}
                alt="Avatar"
                className="bg-preview-img"
                style={{ objectFit: 'cover', borderRadius: '50%', maxWidth: '90px', maxHeight: '90px' }}
              />
            </div>

            <div className="bg-controls-row">
              <input
                ref={avatarFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarFileChange}
                style={{ display: 'none' }}
              />
              <button
                className={`bg-select-btn${avatarUploading ? ' loading' : ''}`}
                onClick={() => avatarFileInputRef.current?.click()}
                disabled={avatarUploading}
              >
                {avatarUploading ? (
                  <><span className="bg-spinner" /> Yükleniyor...</>
                ) : (
                  <>📤 Avatar Seç</>
                )}
              </button>
              {customAvatarImage && (
                <button
                  className="bg-remove-btn"
                  onClick={async () => {
                    if (customAvatarImage.startsWith('/assets')) {
                      await deleteImageFromServer(customAvatarImage);
                    }
                    removeCustomAvatar();
                  }}
                  disabled={avatarUploading}
                >
                  🗑️ Kaldır
                </button>
              )}
            </div>

            <div
              className="bg-toggle-row"
              style={{ pointerEvents: avatarUploading ? 'none' : 'auto' }}
              onClick={() => setCustomAvatarEnabled(!customAvatarEnabled)}
            >
              <div className={`bg-toggle-switch${customAvatarEnabled ? ' on' : ''}`}>
                <div className="bg-toggle-knob" />
              </div>
              <span className="bg-toggle-label">
                {customAvatarEnabled ? 'Özel Avatar Aktif' : 'Özel Avatar Devre Dışı'}
              </span>
            </div>
          </div>
        )}

        {/* ── Long Logo (Footer) ── */}
        {!searchTerm && (
          <div className="bg-upload-section">
            <div className="bg-section-title">
              <div className="bg-section-title-icon"><MdOutlineImage size={15} /></div>
              Long Logo (Footer)
            </div>

            <div
              className="bg-toggle-row"
              onClick={() => setLongLogoVisible(!longLogoVisible)}
            >
              <div className={`bg-toggle-switch${longLogoVisible ? ' on' : ''}`}>
                <div className="bg-toggle-knob" />
              </div>
              <span className="bg-toggle-label">
                {longLogoVisible ? 'Long Logo Görünür' : 'Long Logo Gizli'}
              </span>
            </div>

            <div className={`long-logo-variant-row${!longLogoVisible ? ' disabled' : ''}`}>
              <span className="long-logo-variant-label">Logo Rengi</span>
              <div className="long-logo-variant-group" role="group" aria-label="Long logo rengi">
                <button
                  type="button"
                  className={`long-logo-variant-btn${longLogoVariant !== 'white' ? ' active' : ''}`}
                  onClick={() => setLongLogoVariant('black')}
                  disabled={!longLogoVisible}
                  title="Siyah Logo"
                >
                  <span className="long-logo-variant-swatch swatch-black" aria-hidden="true" />
                  <span>Siyah</span>
                </button>
                <button
                  type="button"
                  className={`long-logo-variant-btn${longLogoVariant === 'white' ? ' active' : ''}`}
                  onClick={() => setLongLogoVariant('white')}
                  disabled={!longLogoVisible}
                  title="Beyaz Logo"
                >
                  <span className="long-logo-variant-swatch swatch-white" aria-hidden="true" />
                  <span>Beyaz</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Sezonluk Mod Seçici ── */}
        {!searchTerm && (
          <div className="bg-upload-section">
            <div className="bg-section-title">
              <div className="bg-section-title-icon"><MdOutlineAcUnit size={15} /></div>
              Sezonluk Mod
            </div>

            <div className="seasonal-mode-grid">
              {SEASONAL_MODES.map((m) => {
                const active = seasonalMode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`seasonal-mode-card${active ? ' active' : ''}`}
                    onClick={() => setSeasonalMode(m.id)}
                    title={m.title}
                  >
                    <span className="seasonal-mode-emoji">{m.emoji}</span>
                    <span className="seasonal-mode-label">{m.label}</span>
                    {active && (
                      <div className="seasonal-mode-active-mark">
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <p className="seasonal-mode-hint">
              {seasonalMode === 'none'
                ? 'Aktif sezonluk efekt yok. Bir mod seç ve "CSS Kaydet" ile kaydet.'
                : `${SEASONAL_MODES.find(m => m.id === seasonalMode)?.emoji} ${SEASONAL_MODES.find(m => m.id === seasonalMode)?.title} aktif. Kaydetmek için "CSS Kaydet" butonuna basın.`}
            </p>
          </div>
        )}

        {/* ── Grid Yerleşimi ── */}
        {!searchTerm && (
          <div className="bg-upload-section">
            <div className="bg-section-title">
              <div className="bg-section-title-icon"><MdOutlineCategory size={15} /></div>
              Grid Düzeni (Kolon x Satır)
            </div>
            <div className="card-layout-toggle" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['3x1', '4x1', '5x1', '6x1'].map((layout) => {
                const cols = layout.split('x')[0];
                const active = String(themeValues.bonusCard_gridCols || '4') === cols;
                return (
                  <button
                    key={layout}
                    type="button"
                    className={`card-layout-option${active ? ' active' : ''}`}
                    onClick={() => updateThemeValue('bonusCard_gridCols', cols)}
                    style={{ padding: '8px', minHeight: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <span className="card-layout-option-label" style={{ fontSize: '14px', fontWeight: 'bold' }}>{layout}</span>
                  </button>
                );
              })}
            </div>
            <p className="seasonal-mode-hint">
              Masaüstü cihazlarda grid üzerinde yan yana kaç adet bonus kartı listeleneceğini belirler.
            </p>
          </div>
        )}

        {/* ── Bonus Kart Tasarımı ── */}
        {!searchTerm && (
          <>
            <div className="bg-upload-section">
              <div className="bg-section-title">
                <div className="bg-section-title-icon"><MdOutlineImage size={15} /></div>
                Bonus Kart Tasarımı
              </div>

              <div className="card-layout-toggle">
                <button
                  type="button"
                  className={`card-layout-option${cardLayoutMode !== 'text-only' ? ' active' : ''}`}
                  onClick={() => setCardLayoutMode('image')}
                  title="Görselli kartlar"
                >
                  <span className="card-layout-option-icon"><MdOutlineImage size={20} /></span>
                  <span className="card-layout-option-label">Görselli</span>
                  <span className="card-layout-option-desc">Klasik tasarım — bonus görseli kart üzerinde</span>
                  {cardLayoutMode !== 'text-only' && (
                    <span className="card-layout-active-mark">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  className={`card-layout-option${cardLayoutMode === 'text-only' ? ' active' : ''}`}
                  onClick={() => setCardLayoutMode('text-only')}
                  title="Görselsiz kartlar"
                >
                  <span className="card-layout-option-icon"><MdOutlineLabel size={20} /></span>
                  <span className="card-layout-option-label">Görselsiz</span>
                  <span className="card-layout-option-desc">Kategori rozeti + başlık + buton (kompakt liste)</span>
                  {cardLayoutMode === 'text-only' && (
                    <span className="card-layout-active-mark">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </span>
                  )}
                </button>
              </div>

              <p className="seasonal-mode-hint">
                {cardLayoutMode === 'text-only'
                  ? 'Görselsiz tasarım aktif. Bonus kartları kategori rozeti + başlık + buton olarak listelenir. Kaydetmek için "CSS Kaydet" butonuna basın.'
                  : 'Görselli tasarım aktif (varsayılan). Kaydetmek için "CSS Kaydet" butonuna basın.'}
              </p>
            </div>

            <div className="bg-upload-section">
              <div className="bg-section-title">
                <div className="bg-section-title-icon"><MdOutlineLabel size={15} /></div>
                Kart Kategori Rozeti Konumu
              </div>

              <div className="card-layout-toggle">
                <button
                  type="button"
                  className={`card-layout-option${cardBadgePosition !== 'right' && cardBadgePosition !== 'none' ? ' active' : ''}`}
                  onClick={() => setCardBadgePosition('left')}
                  title="Sol Üst (Varsayılan)"
                >
                  <span className="card-layout-option-label">Sol Üst</span>
                  <span className="card-layout-option-desc">Rozet sol üstte gösterilir</span>
                  {cardBadgePosition !== 'right' && cardBadgePosition !== 'none' && (
                    <span className="card-layout-active-mark">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5" /></svg>
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  className={`card-layout-option${cardBadgePosition === 'right' ? ' active' : ''}`}
                  onClick={() => setCardBadgePosition('right')}
                  title="Sağ Üst"
                >
                  <span className="card-layout-option-label">Sağ Üst</span>
                  <span className="card-layout-option-desc">Rozet sağ üstte gösterilir</span>
                  {cardBadgePosition === 'right' && (
                    <span className="card-layout-active-mark">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5" /></svg>
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  className={`card-layout-option${cardBadgePosition === 'none' ? ' active' : ''}`}
                  onClick={() => setCardBadgePosition('none')}
                  title="Gizli (Hiç olmasın)"
                >
                  <span className="card-layout-option-label">Hiç olmasın</span>
                  <span className="card-layout-option-desc">Kategori rozeti gizlenir</span>
                  {cardBadgePosition === 'none' && (
                    <span className="card-layout-active-mark">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5" /></svg>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Renk Bölümleri ── */}
        {SECTION_ORDER.map((sectionKey) => {
          let properties = themeConfig[sectionKey];
          if (!properties) return null;
          if (sectionKey === 'header') {
            properties = Object.fromEntries(
              Object.entries(properties).filter(([k]) => !k.startsWith('logo'))
            );
            if (Object.keys(properties).length === 0) return null;
          }
          const meta = SECTION_META[sectionKey] || { title: sectionKey, icon: null };
          return (
            <ThemeSection
              key={sectionKey}
              title={meta.title}
              icon={meta.icon}
              sectionKey={sectionKey}
              properties={properties}
              themeValues={themeValues}
              onThemeChange={updateThemeValue}
              defaultExpanded={false}
              searchTerm={searchTerm}
            />
          );
        })}
      </div>

      {/* ── CSS Kaydet Onay Modal ── */}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>CSS Ayarlarını Kaydet</h3>
            </div>
            <div className="modal-content">
              <p>Mevcut CSS ayarlarını kaydetmek istediğinizden emin misiniz?</p>
              <p style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.55)' }}>
                Bu işlem sunucudaki CSS ayarlarını güncelleyecektir.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-cancel-button"
                onClick={() => setShowSaveModal(false)}
                disabled={loading}
              >
                İptal
              </button>
              <button
                className="modal-confirm-button"
                onClick={saveCssToApi}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress size={14} sx={{ color: '#fff' }} />
                    Kaydediliyor...
                  </>
                ) : (
                  'Evet, Kaydet'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sıfırla Onay Modal ── */}
      {showResetModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>Varsayılan Ayarlara Sıfırla</h3>
            </div>
            <div className="modal-content">
              <p>Tüm CSS ayarlarını varsayılan değerlere sıfırlamak istediğinizden emin misiniz?</p>
              <p style={{ fontSize: '12.5px', color: '#fca5a5' }}>
                ⚠️ Bu işlem geri alınamaz! Mevcut ayarlarınız kaybolacaktır.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-cancel-button"
                onClick={() => setShowResetModal(false)}
              >
                İptal
              </button>
              <button
                className="modal-confirm-button reset"
                onClick={handleConfirmedReset}
              >
                Evet, Sıfırla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CssPanelNew;
